import { Link } from 'react-router-dom';
import type { NotificationItem as N, NotificationType } from '../../types/notification.types';

type Props = { notification: N; onClick?: (n: N) => void; compact?: boolean };

const ICON_FOR: Record<NotificationType, string> = {
  JOB_POSTED: '📋',
  JOB_MATCH_FOUND: '🎯',
  PILOT_INVITED: '✉️',
  QUOTE_RECEIVED: '💬',
  QUOTE_ACCEPTED: '✅',
  QUOTE_REJECTED: '❌',
  CHAT_MESSAGE: '💬',
  PAYMENT_RECEIVED: '💳',
  PAYMENT_HELD_ESCROW: '🔒',
  PAYMENT_RELEASED: '💸',
  DELIVERABLE_UPLOADED: '📦',
  REVISION_REQUESTED: '✏️',
  JOB_COMPLETED: '🎉',
  REVIEW_RECEIVED: '⭐',
  DISPUTE_OPENED: '⚠️',
  DISPUTE_UPDATED: '⚠️',
  ADMIN_ALERT: '📣',
  SYSTEM: '📢',
};

const linkFor = (n: N): string | null => {
  switch (n.reference_type) {
    case 'job': return `/client/jobs/${n.reference_id}`;
    case 'quote': return n.notification_type === 'QUOTE_RECEIVED' ? `/client/jobs` : `/pilot/quotes`;
    case 'deliverable': return n.notification_type === 'REVISION_REQUESTED' ? `/pilot/deliverables` : `/client/jobs`;
    case 'review': return `/pilot/reviews`;
    case 'conversation': return `/messages/${n.reference_id}`;
    case 'payment_intent': return `/client/payments`;
    case 'dispute': return `/admin/disputes`;
    default: return null;
  }
};

export const NotificationItemRow = ({ notification, onClick, compact }: Props) => {
  const href = linkFor(notification);
  const body = (
    <div className={`flex items-start gap-3 ${notification.is_read ? 'opacity-70' : ''}`}>
      <span aria-hidden className="mt-0.5 text-lg shrink-0">{ICON_FOR[notification.notification_type] || '🔔'}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-black text-[#000080]">{notification.title}</p>
        <p className={`text-xs text-slate-600 ${compact ? 'line-clamp-2' : ''}`}>{notification.message}</p>
        <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
          {new Date(notification.created_at).toLocaleString()}
          {!notification.is_read ? <span className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-[#FF9933]" /> : null}
        </p>
      </div>
    </div>
  );
  const cls = `block rounded-xl border ${notification.is_read ? 'border-slate-200 bg-white' : 'border-[#FF9933]/30 bg-[#FF9933]/5'} p-3 transition hover:border-[#000080]`;
  if (href) {
    return <Link to={href} onClick={() => onClick?.(notification)} className={cls}>{body}</Link>;
  }
  return <button type="button" onClick={() => onClick?.(notification)} className={`${cls} w-full text-left`}>{body}</button>;
};
